import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../../utils/apiUrl';
import { settingsAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { FiSave, FiBriefcase, FiFileText, FiUpload, FiX, FiInfo, FiLock, FiEye, FiEyeOff } from 'react-icons/fi';
import { Link } from 'react-router-dom';

const API_URL = getApiUrl();

export default function SettingsPage() {
  const [settings, setSettings] = useState({
    // Infos générales
    shop_name: '',
    shop_address: '',
    phone: '',
    email: '',
    website: '',
    logo_url: '',
    // Infos légales
    siret: '',
    company_name: '',
    legal_status: '',
    vat_number: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Password change states
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [savingPassword, setSavingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await settingsAPI.get();
      setSettings(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsAPI.update(settings);
      toast.success('Paramètres enregistrés');
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/media/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setSettings(prev => ({ ...prev, logo_url: data.url || data.file_url }));
        toast.success('Logo uploadé');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
    }
  };

  // Handle password change
  const handlePasswordChange = async () => {
    // Validation
    if (!passwordData.current_password) {
      toast.error('Veuillez entrer votre mot de passe actuel');
      return;
    }
    if (passwordData.new_password.length < 6) {
      toast.error('Le nouveau mot de passe doit contenir au moins 6 caractères');
      return;
    }
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    setSavingPassword(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(passwordData)
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.success('Mot de passe modifié avec succès');
        setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
      } else {
        toast.error(data.detail || 'Erreur lors du changement de mot de passe');
      }
    } catch (error) {
      toast.error('Erreur de connexion au serveur');
    } finally {
      setSavingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Infos Entreprise</h1>
          <p className="text-slate-500 mt-1">Informations de facturation et coordonnées</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-amber-600 hover:bg-amber-700">
          <FiSave className="mr-2 h-4 w-4" />
          {saving ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <FiInfo className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-blue-800">
          <p className="font-medium mb-1">Ces informations apparaissent sur vos factures</p>
          <p>Le logo utilisé sera celui configuré dans le header de votre site. Pour la configuration des paiements et des acomptes, rendez-vous dans <Link to="/dashboard/payment-methods" className="underline font-medium">Moyens de Paiement</Link>.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Infos Générales */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FiBriefcase className="text-amber-600" />
              Informations Générales
            </CardTitle>
            <CardDescription>Coordonnées de votre entreprise</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Nom de l'entreprise</Label>
              <Input
                value={settings.shop_name}
                onChange={(e) => setSettings({ ...settings, shop_name: e.target.value })}
                placeholder="Just A Smile Events"
                className="mt-1"
              />
            </div>

            <div>
              <Label>Logo (aperçu)</Label>
              <div className="mt-1 flex items-center gap-4">
                {settings.logo_url ? (
                  <div className="relative">
                    <img 
                      src={settings.logo_url} 
                      alt="Logo" 
                      className="h-16 w-auto object-contain bg-slate-100 rounded p-2"
                    />
                    <button
                      onClick={() => setSettings({ ...settings, logo_url: '' })}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                    >
                      <FiX className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <label className="flex items-center gap-2 px-4 py-2 border border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-amber-400 transition-colors">
                    <FiUpload className="h-5 w-5 text-slate-400" />
                    <span className="text-sm text-slate-600">Choisir un fichier</span>
                    <input type="file" onChange={handleLogoUpload} className="hidden" accept="image/*" />
                  </label>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Ce logo sera utilisé sur vos factures. Configurez le logo du site dans <Link to="/dashboard/site-settings" className="underline">Paramètres du site</Link>.
              </p>
            </div>

            <div>
              <Label>Adresse</Label>
              <Textarea
                value={settings.shop_address}
                onChange={(e) => setSettings({ ...settings, shop_address: e.target.value })}
                placeholder="23 Av. de la Crau, 13118 Istres"
                rows={2}
                className="mt-1"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Téléphone</Label>
                <Input
                  value={settings.phone}
                  onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
                  placeholder="+33 6 00 00 00 00"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={settings.email}
                  onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                  placeholder="contact@justasmile-events.com"
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label>Site Web</Label>
              <Input
                value={settings.website}
                onChange={(e) => setSettings({ ...settings, website: e.target.value })}
                placeholder="https://www.justasmile-events.com"
                className="mt-1"
              />
            </div>
          </CardContent>
        </Card>

        {/* Infos Légales */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FiFileText className="text-amber-600" />
              Informations Légales
            </CardTitle>
            <CardDescription>Pour vos factures et documents officiels</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Raison sociale</Label>
              <Input
                value={settings.company_name}
                onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
                placeholder="JUST A SMILE EVENTS"
                className="mt-1"
              />
            </div>

            <div>
              <Label>Forme juridique</Label>
              <Input
                value={settings.legal_status}
                onChange={(e) => setSettings({ ...settings, legal_status: e.target.value })}
                placeholder="Micro-entreprise, SARL, SAS..."
                className="mt-1"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>SIRET</Label>
                <Input
                  value={settings.siret}
                  onChange={(e) => setSettings({ ...settings, siret: e.target.value })}
                  placeholder="123 456 789 00012"
                  className="mt-1 font-mono"
                />
              </div>
              <div>
                <Label>N° TVA (optionnel)</Label>
                <Input
                  value={settings.vat_number}
                  onChange={(e) => setSettings({ ...settings, vat_number: e.target.value })}
                  placeholder="FR12345678901"
                  className="mt-1 font-mono"
                />
              </div>
            </div>

            <div className="pt-4 border-t mt-6">
              <p className="text-sm text-slate-500">
                <strong>Note :</strong> Si vous êtes en micro-entreprise et que la TVA n'est pas applicable, 
                laissez le champ TVA vide. La mention "TVA non applicable, art. 293 B du CGI" sera 
                automatiquement ajoutée sur vos factures.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Password Change Section */}
        <Card className="shadow-sm border border-slate-200">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <FiLock className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Sécurité du compte</CardTitle>
                <CardDescription>Modifier votre mot de passe</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 max-w-md">
              <div>
                <Label>Mot de passe actuel</Label>
                <div className="relative mt-1">
                  <Input
                    type={showCurrentPassword ? "text" : "password"}
                    value={passwordData.current_password}
                    onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                    placeholder="••••••••"
                    data-testid="current-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showCurrentPassword ? <FiEyeOff className="w-4 h-4" /> : <FiEye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              <div>
                <Label>Nouveau mot de passe</Label>
                <div className="relative mt-1">
                  <Input
                    type={showNewPassword ? "text" : "password"}
                    value={passwordData.new_password}
                    onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                    placeholder="••••••••"
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showNewPassword ? <FiEyeOff className="w-4 h-4" /> : <FiEye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-slate-500 mt-1">Minimum 6 caractères</p>
              </div>
              
              <div>
                <Label>Confirmer le nouveau mot de passe</Label>
                <Input
                  type="password"
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                  placeholder="••••••••"
                  className="mt-1"
                  data-testid="confirm-password-input"
                />
              </div>
              
              <Button 
                onClick={handlePasswordChange} 
                disabled={savingPassword}
                className="bg-red-600 hover:bg-red-700"
                data-testid="change-password-btn"
              >
                {savingPassword ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Modification...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <FiLock className="w-4 h-4" />
                    Modifier le mot de passe
                  </span>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
